import React from 'react';

import { SeoSlugEnum } from '@rover/constants/src/search/seo.constants';
import MQ from '@rover/kibble/styles/MediaQuery';
import useRedirectToCorrectCountryPage from '@rover/react-lib/src/hooks/useRedirectToCorrectCountryPage';
import roverHomepageHeroDesktop from '@rover/react-lib/src/images/homepage/rover_homepage_hero_desktop.webp';
import roverHomepageHeroTablet from '@rover/react-lib/src/images/homepage/rover_homepage_hero_tablet.webp';
import httpContext from '@rover/rsdk/src/modules/Network/httpContext';
import useCitiesAndNeighborhodsShim from '@rover/shared/js/hooks/useCitiesAndNeighborhoodsShim';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useCountryFooterLinksShim from '@rover/shared/js/hooks/useCountryFooterLinksShim';
import useCountryRegionLinksShim from '@rover/shared/js/hooks/useCountryRegionLinksShim';
import useHomePageConfigurationShim from '@rover/shared/js/hooks/useHomePageConfigurationShim';
import { getURL } from '@rover/shared/js/utils/getFullUrl';
import { referrerIsRover } from '@rover/shared/js/utils/referrer';
import { pickPreferredLanguage } from '@rover/utilities/acceptLanguage';
import {
  CountryUrlPrefix,
  getCountryHomepagePath,
  getCountryUrlPrefix,
  getShortLanguagesByUrlPrefix,
  US_COUNTRY_URL_PREFIX,
} from '@rover/utilities/roverGeo.constants';
import { redirect } from '@rover/utilities/url';

import DefaultBrandHomePageTemplate from './DefaultBrandHomePageTemplate';
import { effects as homePageEffects } from './HomePage.duck';

function redirectUsToIndex(): void {
  const location = getURL();
  const countryUrlPrefix = getCountryUrlPrefix(location.pathname);

  // always redirect `/us/` to `/`
  if (countryUrlPrefix === US_COUNTRY_URL_PREFIX) {
    // same exact location, just trim off the leading bit
    location.pathname = location.pathname.slice('/us'.length);
    if (process.env.JS_ENV_SERVER) {
      // reconstruct just the path because in dev `location.host` is incorrect
      redirect(location.pathname + location.search + location.hash);
    }
  }
}

// Get user's preferred language directly from `Accept-Language` header,
// and pick the best match from the target country's supported languages.
function getPreferredRedirectLanguage(countryUrlPrefix: CountryUrlPrefix): string | undefined {
  if (!process.env.JS_ENV_SERVER) return undefined;
  const acceptLanguage = httpContext.getReq()?.header('Accept-Language');
  return pickPreferredLanguage(acceptLanguage, getShortLanguagesByUrlPrefix(countryUrlPrefix));
}

function useRedirectToCountrySpecificHomepage(): void {
  useRedirectToCorrectCountryPage({
    canRedirect(requestCountryUrlPrefix) {
      // don't redirect non-US users coming to the US index from the site
      if (referrerIsRover() && requestCountryUrlPrefix === null) {
        return false;
      }
      return true;
    },
    getNewPath(currentPath, nextCountryUrlPrefix) {
      const preferredLanguage = getPreferredRedirectLanguage(nextCountryUrlPrefix);
      return getCountryHomepagePath(nextCountryUrlPrefix, preferredLanguage);
    },
  });
}

const HomePageContainer: React.FC & { earlyPrefetchData: any; preloadAssets: any } = () => {
  const { metaTags } = useHomePageConfigurationShim();
  const { results: countryFooterLinks } = useCountryFooterLinksShim();
  const countryRegionLinks = useCountryRegionLinksShim();
  const { results: citiesAndNeighborhoodsLinks } = useCitiesAndNeighborhodsShim(
    SeoSlugEnum.DOG_BOARDING
  );
  const { current } = useCountryConfigurationShim();

  redirectUsToIndex();
  useRedirectToCountrySpecificHomepage();

  return (
    <DefaultBrandHomePageTemplate
      metaTags={metaTags}
      countryFooterLinks={countryFooterLinks}
      countryRegionLinks={countryRegionLinks}
      citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
      roverGuaranteeLink={current?.roverGuaranteeUrl}
      cxOperatingHours={current?.cxOperatingHours ?? undefined}
    />
  );
};

HomePageContainer.earlyPrefetchData = () => homePageEffects.prefetchData;

// preload the images rendered by `BackgroundImage`
// in `src/frontend/react-lib/src/components/heroes/Hero/Hero.tsx`
// using the appropriate media query so only one image gets preloaded
HomePageContainer.preloadAssets = [
  {
    href: roverHomepageHeroTablet,
    rel: 'preload',
    as: 'image',
    media: MQ.SM_DOWN,
  },
  {
    href: roverHomepageHeroDesktop,
    rel: 'preload',
    as: 'image',
    media: MQ.MD_UP,
  },
];

export default HomePageContainer;
